// src/pages/Checkout.jsx
import { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";

const EMAILJS_SERVICE = "service_m962lst";
const EMAILJS_TEMPLATE = "template_oes7mci";
const EMAILJS_KEY = "cE4Pi_BUsCNs1yMfx";

const COUNTRIES = [
  { value: "Kosovë", label: "🇽🇰  Kosovë", shipping: 0 },
  { value: "Shqipëri", label: "🇦🇱  Shqipëri", shipping: 3 },
  { value: "Maqedoni", label: "🇲🇰  Maqedoni", shipping: 3 },
];

/* ── Global ─────────────────────────────────────────────────── */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Figtree:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f7f8fc; }
  ::selection { background: #1a56ff22; }
`;

const C = {
  bg: "#f7f8fc",
  white: "#ffffff",
  blue: "#578bda",
  blueSoft: "#eef2ff",
  blueDeep: "#578bda",
  text: "#0d0d0d",
  sub: "#6b7280",
  border: "#e8eaed",
  danger: "#ef4444",
  green: "#16a34a",
  greenSoft: "#f0fdf4",
};

/* ── Spin ───────────────────────────────────────────────────── */
const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

/* ── Layout ─────────────────────────────────────────────────── */
const Page = styled.div`
  min-height: 100vh;
  background: ${C.bg};
  font-family: "Montserrat", sans-serif;
  padding: 10em 48px 80px;

  @media (max-width: 768px) {
    padding: 8em 20px 60px;
  }
`;

const Wrap = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const BackBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${C.sub};
  background: ${C.white};
  border: 1.5px solid ${C.border};
  border-radius: 100px;
  padding: 8px 16px;
  text-decoration: none;
  transition: all 0.18s;
  margin-bottom: 32px;
  &:hover {
    border-color: ${C.blue};
    color: ${C.blue};
    background: ${C.blueSoft};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 24px;
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

/* ── Card shell ─────────────────────────────────────────────── */
const Card = styled(motion.div)`
  background: ${C.white};
  border: 1.5px solid ${C.border};
  border-radius: 20px;
  padding: 28px;
`;

/* ── Product summary ────────────────────────────────────────── */
const ProductImg = styled.div`
  width: 100%;
  aspect-ratio: 4/3;
  border-radius: 14px;
  overflow: hidden;
  background: ${C.bg};
  margin-bottom: 20px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;

const ProductName = styled.h2`
  font-family: "Montserrat", sans-serif;
  font-size: 18px;
  text-transform: capitalize;
  font-weight: 800;
  color: ${C.text};
  letter-spacing: -0.02em;
  margin-bottom: 6px;
`;

const BrandPill = styled.span`
  display: inline-block;
  background: ${C.blueSoft};
  color: ${C.blue};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border-radius: 100px;
  padding: 4px 12px;
  margin-bottom: 20px;
`;

const SummaryDivider = styled.div`
  height: 1.5px;
  background: ${C.border};
  margin: 18px 0;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: ${C.sub};
  margin-bottom: 10px;

  span:last-child {
    color: ${C.text};
    font-weight: 600;
  }

  &.total {
    font-size: 16px;
    font-weight: 700;
    color: ${C.text};
    margin-top: 4px;
    span:last-child {
      color: ${C.blue};
      font-family: "Syne", sans-serif;
      font-size: 20px;
      font-weight: 800;
    }
  }

  &.free span:last-child {
    color: ${C.green};
  }
`;

/* ── Form ───────────────────────────────────────────────────── */
const FormTitle = styled.h2`
  font-family: "Montserrat", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${C.text};
  letter-spacing: -0.02em;
  margin-bottom: 22px;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: "";
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${C.blue};
  }
`;

const FieldGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  margin-bottom: 12px;

  label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${C.sub};
    margin-bottom: 7px;
  }

  input,
  select,
  textarea {
    width: 100%;
    background: #fafafa;
    border: 1.5px solid ${({ $error }) => ($error ? C.danger : C.border)};
    border-radius: 12px;
    padding: 11px 14px;
    font-family: "Montserrat", sans-serif;
    font-size: 14px;
    color: ${C.text};
    outline: none;
    transition:
      border-color 0.18s,
      box-shadow 0.18s,
      background 0.18s;

    &:focus {
      border-color: ${C.blue};
      background: #fff;
      box-shadow: 0 0 0 3px #1a56ff15;
    }
    &::placeholder {
      color: #c0c4cc;
    }
  }

  select {
    appearance: none;
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
    background-color: #fafafa;
  }

  textarea {
    resize: none;
    height: 90px;
    line-height: 1.5;
  }

  .err {
    font-size: 11px;
    color: ${C.danger};
    margin-top: 5px;
    font-weight: 500;
  }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 15px;
  height: 15px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  vertical-align: middle;
  margin-right: 8px;
`;

const SubmitBtn = styled(motion.button)`
  width: 100%;
  margin-top: 6px;
  padding: 15px;
  background: ${C.blue};
  border: none;
  border-radius: 14px;
  font-family: "Figtree", sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition:
    background 0.18s,
    box-shadow 0.18s;

  &:hover:not(:disabled) {
    background: ${C.blueDeep};
    box-shadow: 0 8px 24px #1a56ff30;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorBanner = styled(motion.div)`
  background: #fef2f2;
  border: 1.5px solid #fecaca;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 13px;
  color: ${C.danger};
  font-weight: 500;
  margin-bottom: 16px;
`;

/* ── Success screen ─────────────────────────────────────────── */
const SuccessWrap = styled(motion.div)`
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
`;

const SuccessIcon = styled(motion.div)`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${C.greenSoft};
  border: 2px solid #bbf7d0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  margin-bottom: 24px;
`;

const SuccessTitle = styled.h1`
  font-family: "Syne", sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: ${C.text};
  letter-spacing: -0.03em;
  margin-bottom: 10px;
`;

const SuccessSub = styled.p`
  font-size: 15px;
  color: ${C.sub};
  max-width: 380px;
  line-height: 1.6;
  margin-bottom: 32px;
`;

const SuccessBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${C.blue};
  color: white;
  font-family: "Figtree", sans-serif;
  font-size: 14px;
  font-weight: 700;
  padding: 14px 28px;
  border-radius: 14px;
  text-decoration: none;
  transition: background 0.18s;
  &:hover {
    background: ${C.blueDeep};
  }
`;

/* ── No product fallback ────────────────────────────────────── */
const NoProduct = styled.div`
  text-align: center;
  padding: 80px 20px;

  h2 {
    font-family: "Syne", sans-serif;
    font-size: 20px;
    font-weight: 800;
    color: ${C.text};
    margin-bottom: 8px;
  }
  p {
    font-size: 14px;
    color: ${C.sub};
    margin-bottom: 24px;
  }
`;

/* ── Helpers ────────────────────────────────────────────────── */
const EMPTY = {
  name: "",
  phone: "",
  country: "",
  city: "",
  address: "",
  message: "",
};

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = "Required";
  if (!form.phone.trim()) errors.phone = "Required";
  if (!form.country) errors.country = "Required";
  if (!form.city.trim()) errors.city = "Required";
  if (!form.address.trim()) errors.address = "Required";
  return errors;
}

/* ── Component ──────────────────────────────────────────────── */
export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();

  // product + qty passed from ProductDetail via navigate state
  const { product, qty = 1 } = location.state || {};

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSub] = useState(false);
  const [submitErr, setSubErr] = useState("");
  const [success, setSuccess] = useState(false);

  const country = COUNTRIES.find((c) => c.value === form.country);
  const shipping = country ? country.shipping : 0;
  const subtotal = product ? product.price * qty : 0;
  const total = subtotal + shipping;
  const isFree = shipping === 0;

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: "" }));
  };

  const handleSubmit = async () => {
    const errs = validate(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    if (!product) return;

    setSub(true);
    setSubErr("");

    const templateParams = {
      buyer_name: form.name,
      buyer_phone: form.phone,
      buyer_country: form.country,
      buyer_city: form.city,
      buyer_address: form.address,
      buyer_message: form.message || "—",
      product_name: product.name,
      product_brand: product.brand,
      product_price: `€${Number(product.price).toFixed(2)}`,
      product_qty: qty,
      product_subtotal: `€${subtotal.toFixed(2)}`,
      shipping_cost: isFree ? "Free" : `€${shipping.toFixed(2)}`,
      order_total: `€${total.toFixed(2)}`,
    };

    try {
      await emailjs.send(
        EMAILJS_SERVICE,
        EMAILJS_TEMPLATE,
        templateParams,
        EMAILJS_KEY,
      );
      setSuccess(true);
    } catch (err) {
      setSubErr(
        "Something went wrong. Please try again or contact us directly.",
      );
    } finally {
      setSub(false);
    }
  };

  /* ── Success screen ── */
  if (success)
    return (
      <>
        <GlobalStyle />
        <Page>
          <Wrap>
            <SuccessWrap
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <SuccessIcon
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 20,
                  delay: 0.15,
                }}
              >
                ✓
              </SuccessIcon>
              <SuccessTitle>Porosia u konfirmua!</SuccessTitle>
              <SuccessSub>
                Faleminderit, <strong>{form.name}</strong>! Porosia juaj është
                konfirmuar. Do të ju kontaktoj posta në numrin{" "}
                <strong>{form.phone}</strong> për ta pranuar porosinë.
              </SuccessSub>
              <SuccessBtn to="/products">← Shiko Produkte tjera</SuccessBtn>
            </SuccessWrap>
          </Wrap>
        </Page>
      </>
    );

  /* ── No product passed ── */
  if (!product)
    return (
      <>
        <GlobalStyle />
        <Page>
          <Wrap>
            <NoProduct>
              <h2>No product selected</h2>
              <p>Please go back and select a product first.</p>
              <SubmitBtn
                as={Link}
                to="/products"
                style={{
                  width: "auto",
                  padding: "13px 28px",
                  margin: "0 auto",
                }}
              >
                Browse Products
              </SubmitBtn>
            </NoProduct>
          </Wrap>
        </Page>
      </>
    );

  return (
    <>
      <GlobalStyle />
      <Page>
        <Wrap>
          <BackBtn to={`/products/${product.id}`}>← Back</BackBtn>

          <Grid>
            {/* ── Left: product summary ── */}
            <Card
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <ProductImg>
                <img src={product.imageUrl} alt={product.name} />
              </ProductImg>

              <BrandPill>{product.brand}</BrandPill>
              <ProductName>{product.name}</ProductName>

              <SummaryDivider />

              <SummaryRow>
                <span>Cmimi</span>
                <span>€{Number(product.price).toFixed(2)}</span>
              </SummaryRow>
              <SummaryRow>
                <span>Sasia</span>
                <span>{qty}×</span>
              </SummaryRow>
              <SummaryRow>
                <span>Subtotali</span>
                <span>€{subtotal.toFixed(2)}</span>
              </SummaryRow>
              <SummaryRow className={isFree ? "free" : ""}>
                <span>Transporti</span>
                <span>
                  {form.country
                    ? isFree
                      ? "FALAS 🎉"
                      : `€${shipping.toFixed(2)}`
                    : "—"}
                </span>
              </SummaryRow>

              <SummaryDivider />

              <SummaryRow className="total">
                <span>Totali</span>
                <span>€{total.toFixed(2)}</span>
              </SummaryRow>
            </Card>

            {/* ── Right: form ── */}
            <Card
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.45,
                ease: [0.22, 1, 0.36, 1],
                delay: 0.06,
              }}
            >
              <FormTitle>Transporti</FormTitle>

              <AnimatePresence>
                {submitErr && (
                  <ErrorBanner
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                  >
                    {submitErr}
                  </ErrorBanner>
                )}
              </AnimatePresence>

              <FieldGroup>
                <Field $error={!!errors.name}>
                  <label>Emri i plote</label>
                  <input
                    placeholder="Fisnik Krasniqi"
                    value={form.name}
                    onChange={set("name")}
                  />
                  {errors.name && <p className="err">{errors.name}</p>}
                </Field>
                <Field $error={!!errors.phone}>
                  <label>Numri i telefonit</label>
                  <input
                    placeholder="+383 44 000 000"
                    value={form.phone}
                    onChange={set("phone")}
                  />
                  {errors.phone && <p className="err">{errors.phone}</p>}
                </Field>
              </FieldGroup>

              <Field $error={!!errors.country}>
                <label>Shteti</label>
                <select value={form.country} onChange={set("country")}>
                  <option value="">Shteti</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                {errors.country && <p className="err">{errors.country}</p>}
              </Field>

              <FieldGroup>
                <Field $error={!!errors.city}>
                  <label>Qyteti</label>
                  <input
                    placeholder="Prishtinë"
                    value={form.city}
                    onChange={set("city")}
                  />
                  {errors.city && <p className="err">{errors.city}</p>}
                </Field>
                <Field $error={!!errors.address}>
                  <label>Adresa</label>
                  <input
                    placeholder="Rr. Agim Ramadani, nr. 5"
                    value={form.address}
                    onChange={set("address")}
                  />
                  {errors.address && <p className="err">{errors.address}</p>}
                </Field>
              </FieldGroup>

              <Field>
                <label>
                  Mesazhi{" "}
                  <span
                    style={{
                      fontWeight: 400,
                      textTransform: "none",
                      letterSpacing: 0,
                    }}
                  >
                    (opcional)
                  </span>
                </label>
                <textarea
                  placeholder="Any notes for your order…"
                  value={form.message}
                  onChange={set("message")}
                />
              </Field>

              <SubmitBtn
                onClick={handleSubmit}
                disabled={submitting}
                whileTap={{ scale: 0.98 }}
              >
                {submitting ? (
                  <>
                    <Spinner />
                    Duke u konfirmuar
                  </>
                ) : (
                  <>Konfirmo Porosinë · €{total.toFixed(2)}</>
                )}
              </SubmitBtn>
            </Card>
          </Grid>
        </Wrap>
      </Page>
    </>
  );
}
